/**
 * Voolist Chrome Extension Bridge
 *
 * Utility for the Voolist React app to communicate with the Chrome extension.
 * Uses window.postMessage to send requests to the extension's content script
 * (voolist-auth-script.js) running on the Voolist app domain.
 *
 * Usage:
 *   import { voolistExtension } from 'src/utils/voolist-extension';
 *
 *   // Check if extension is installed (page load: cached, retries the load race)
 *   const installed = await voolistExtension.isInstalled();
 *
 *   // Check it is reachable right now (immediately before publishing: one try, no cache)
 *   const { installed, message } = await voolistExtension.ping();
 *
 *   // Crosslist a listing to Vinted
 *   const result = await voolistExtension.crosslist('vinted', listingData);
 *
 *   // Get Vinted categories
 *   const catalogs = await voolistExtension.getVintedCatalogs();
 */

import {
  everyExtensionKnowsMarketplace,
  extensionKnowsMarketplace,
  updateExtensionMessage,
} from 'src/utils/extension-marketplace-support';
import { takedownHidesPost } from 'src/utils/extension-publish-rules';

/**
 * The capability a build names when it takes a post off sale the marketplace's own way
 * (VOOLIST_EXTENSION_DELIST_LISTING): deleted, or on a marketplace whose takedown hides the post, hidden.
 */
const DELIST_CAPABILITY = 'delistListing';

// How long to wait with NO sign of life before giving up. Not a total budget: any progress
// message from the extension restarts it, so an operation that keeps reporting steps is
// allowed to take as long as it honestly needs.
const RESPONSE_TIMEOUT = 60000;
const PING_TIMEOUT = 2000; // 2 seconds for ping
const CONNECT_TIMEOUT = 15000; // 15 seconds for auto-connect fast path

/**
 * The rejection every caller gets when the extension stops answering.
 *
 * Carries `isTimeout` so a caller can tell "went quiet" from any other failure without
 * matching on the sentence — the distinction decides whether a half-finished marketplace
 * create may be reported as failed, so it must not rest on wording.
 */
export function extensionTimeoutError () {
  const error = new Error("The Voolist extension didn't respond. Check that it's installed and turned on, then try again.");
  error.isTimeout = true;
  return error;
}

// --- Detection cache ---
const CACHE_TTL_MS = 30_000; // 30 seconds
let _installedCache = { value: null, timestamp: 0 };

/**
 * Send a message to the extension and wait for a response.
 *
 * `timeout` is an INACTIVITY window, not a total budget: every progress message the
 * extension sends for this request restarts it. A long operation that keeps reporting its
 * steps is therefore never abandoned for being slow — only for going quiet, which is the
 * thing the caller actually needs to distinguish.
 *
 * @param {string} type - Message type (VOOLIST_EXTENSION_*)
 * @param {Object} data - Additional data to send
 * @param {number} timeout - Milliseconds of silence before giving up
 * @param {Function} [onProgress] - Called with `{ step, detail }` for each progress message
 * @returns {Promise<Object>}
 */
function sendToExtension (type, data = {}, timeout = RESPONSE_TIMEOUT, onProgress) {
  return new Promise((resolve, reject) => {
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    let timer;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        window.removeEventListener('message', handler);
        reject(extensionTimeoutError());
      }, timeout);
    };

    const handler = (event) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data || event.data.requestId !== requestId) return;

      // Progress messages are a sign of life: they restart the clock but don't resolve.
      if (event.data.type === 'VOOLIST_EXTENSION_PROGRESS') {
        resetTimer();
        if (onProgress) {
          try {
            onProgress({ step: event.data.step, detail: event.data.detail });
          } catch (e) {
            // A caller's progress handler must never sink the operation reporting to it.
          }
        }
        return;
      }

      if (event.data.type !== 'VOOLIST_EXTENSION_RESPONSE') return;

      // Got our response
      window.removeEventListener('message', handler);
      clearTimeout(timer);

      // Remove the internal fields before returning
      const { type: _, requestId: __, ...result } = event.data;
      resolve(result);
    };

    window.addEventListener('message', handler);
    resetTimer();

    // Send the message
    window.postMessage({
      type,
      requestId,
      ...data
    }, window.location.origin);
  });
}

/**
 * Send a call that names a marketplace — unless the installed extension does not know that
 * marketplace (voolistExtension.knowsMarketplace). Then nothing is sent, and the call answers
 * `{ success: false, extensionOutdated: true, error }` with the sentence that tells the seller to
 * update. An older build would not answer a message it does not know at all, and the seller would
 * wait out a timeout for a failure that says nothing.
 *
 * @param {string} platform - The marketplace the call works on
 * @param {string} type - Message type (VOOLIST_EXTENSION_*)
 * @param {Object} data
 * @param {number} [timeout]
 * @param {Function} [onProgress]
 * @returns {Promise<Object>}
 */
async function sendForMarketplace (platform, type, data, timeout = RESPONSE_TIMEOUT, onProgress) {
  const { known, message } = await voolistExtension.knowsMarketplace(platform);
  if (!known) return { success: false, extensionOutdated: true, error: message };
  return sendToExtension(type, data, timeout, onProgress);
}

/**
 * Voolist Extension API
 */
export const voolistExtension = {
  /**
   * Check if Voolist Chrome extension is installed and responding
   * @returns {Promise<boolean>}
   */
  async isInstalled () {
    // Return cached result if fresh
    const now = Date.now();
    if (_installedCache.value === true && (now - _installedCache.timestamp) < CACHE_TTL_MS) {
      return true;
    }

    // Try up to 3 times with increasing delays (handles content script load race)
    const ping = (delay) =>
      new Promise(resolve => setTimeout(resolve, delay))
        .then(() => sendToExtension('VOOLIST_EXTENSION_PING', {}, PING_TIMEOUT))
        .then(result => !!(result && result.installed))
        .catch(() => false);

    const installed = await ping(0) || await ping(500) || await ping(1000);
    if (installed) {
      _installedCache = { value: true, timestamp: Date.now() };
    }
    return installed;
  },

  /**
   * Ask the extension whether it is reachable right now — one attempt, never cached.
   *
   * Call this immediately before an action the seller has already committed to (pressing Save),
   * rather than trusting an answer taken when the page loaded. `isInstalled()` answers the
   * page-load question and is wrong for this one in both directions: it short-circuits on a
   * cached yes for 30s, so a publish can be handed to an extension that stopped listening
   * minutes ago, and when the answer is no it spends ~7.5s on three spaced retries — a race
   * that is long over by submit time — for an answer a single ping gives in 2s.
   *
   * @param {Object} [options]
   * @param {number} [options.timeoutMs] - How long to wait for a reply
   * @returns {Promise<{installed: boolean, version?: string, message?: string}>} `message` is the
   *   extension's own words for the seller, present only when it sent any. The one that matters is
   *   "Extension was updated. Please refresh this page." — it names the only thing that fixes
   *   that state, so it must reach the seller rather than be flattened into a bare false.
   *   `version` is the extension's own version, and comes back ONLY from a reply that answered —
   *   an extension that has stopped responding cannot tell us which version it was, so a caller
   *   that wants that (to remember it while it can) must never be handed one alongside a `false`.
   */
  async ping ({ timeoutMs = PING_TIMEOUT } = {}) {
    try {
      const result = await sendToExtension('VOOLIST_EXTENSION_PING', {}, timeoutMs);

      if (result?.installed) {
        // Only ever record a yes: `isInstalled()` short-circuits on a cached `true`, so this
        // refreshes its cache without being able to change what it answers.
        _installedCache = { value: true, timestamp: Date.now() };
        return { installed: true, version: result.version };
      }

      // The content script answered but could not serve the ping — it puts its message for the
      // seller in `error` (voolist-auth-script.js), which is where the update notice arrives.
      return result?.error
        ? { installed: false, message: result.error }
        : { installed: false };
    } catch {
      // Nothing answered at all (no extension, or it is not listening). Never throw: the caller
      // is deciding whether to publish, and there is no message from the extension to pass on.
      return { installed: false };
    }
  },

  /**
   * What the installed extension says it can do — `'depopSalesCheck'`, say — from its answer to a PING.
   * Ask for a capability rather than compare version numbers: a version number moves with every
   * release and says nothing about what a build holds.
   *
   * @returns {Promise<string[]|null>} The extension's capabilities: an empty list from an extension
   *   that answered without naming any, which is every build from before capabilities were named; or
   *   null when no extension answered — none installed, or one that has stopped listening — so nothing
   *   is known about what it can do.
   */
  async getCapabilities () {
    try {
      const result = await sendToExtension('VOOLIST_EXTENSION_PING', {}, PING_TIMEOUT);
      if (!result?.installed) return null;
      return Array.isArray(result.capabilities) ? result.capabilities : [];
    } catch {
      return null;
    }
  },

  /**
   * Whether the installed extension can work on a marketplace — asked before every call that names
   * one. Every build works on Poshmark and Depop, so those answer at once, with no ping. A marketplace
   * added later is known only to the builds released with it, which name it among their capabilities.
   *
   * @param {string} platform
   * @returns {Promise<{known: boolean, message: (string|null)}>} `message` is what the seller reads when
   *   the extension does not know it. No extension answering counts as known: telling the seller to
   *   install one is the availability check's job, not this one's.
   */
  async knowsMarketplace (platform) {
    if (everyExtensionKnowsMarketplace(platform)) return { known: true, message: null };
    const capabilities = await voolistExtension.getCapabilities();
    return extensionKnowsMarketplace(capabilities, platform)
      ? { known: true, message: null }
      : { known: false, message: updateExtensionMessage(platform) };
  },

  /**
   * Get full extension status (installed, version, update availability)
   * @returns {Promise<{installed: boolean, version: string|null, updateAvailable: boolean, updateVersion: string|null}>}
   */
  async getStatus () {
    try {
      const result = await sendToExtension('VOOLIST_EXTENSION_PING', {}, PING_TIMEOUT);
      const installed = !!result?.installed;
      if (installed) {
        _installedCache = { value: true, timestamp: Date.now() };
      }
      return {
        installed,
        version: result?.version || null,
        updateAvailable: result?.updateAvailable || false,
        updateVersion: result?.updateVersion || null
      };
    } catch {
      return { installed: false, version: null, updateAvailable: false, updateVersion: null };
    }
  },

  /**
   * Get platform connection statuses from the extension
   * @returns {Promise<Object>} { success, statuses, isVoolistLoggedIn }
   */
  async getPlatformStatuses () {
    return sendToExtension('VOOLIST_EXTENSION_GET_PLATFORM_STATUSES');
  },

  // ==================== Crosslisting ====================

  /**
   * Crosslist a listing to a marketplace platform
   * @param {string} platform - Target platform ('vinted', 'depop', etc.)
   * @param {Object} listing - Listing data including images, title, etc.
   * @param {Object} [options]
   * @param {number} [options.timeoutMs] - Milliseconds of SILENCE before giving up. The
   *   extension reports each step of a create, so this is not a cap on how long the
   *   create may take — only on how long it may say nothing.
   * @param {Function} [options.onProgress] - Called with `{ step, detail }` as the create proceeds
   * @returns {Promise<Object>} { success, item, vintedItemId, error }
   */
  async crosslist (platform, listing, { timeoutMs = RESPONSE_TIMEOUT, onProgress } = {}) {
    return sendForMarketplace(platform, 'VOOLIST_EXTENSION_CROSSLIST', { platform, listing }, timeoutMs, onProgress);
  },

  /**
   * Update a listing on a marketplace platform
   * @param {string} platform - Target platform
   * @param {string|number} itemId - Platform item ID
   * @param {Object} listing - Updated listing data
   * @param {Object} [oldListing] - Previous listing data for change detection
   * @param {Object} [options]
   * @param {number} [options.timeoutMs] - Milliseconds of silence before giving up
   * @param {Function} [options.onProgress] - Called with `{ step, detail }` as the update proceeds
   * @returns {Promise<Object>}
   */
  async updateListing (platform, itemId, listing, oldListing, { timeoutMs = RESPONSE_TIMEOUT, onProgress } = {}) {
    return sendForMarketplace(platform, 'VOOLIST_EXTENSION_UPDATE_LISTING', { platform, itemId, listing, oldListing }, timeoutMs, onProgress);
  },

  /**
   * Delete a listing from a marketplace platform — what Delist & Relist does before it posts the item
   * afresh, on every marketplace, Whatnot included. Voolist never deletes an OfferUp listing: there the
   * extension archives it and Voolist retires its link, and it first checks that OfferUp would take the new
   * post — the one `replacement` names — before anything comes down. Other marketplaces ignore `replacement`
   * and `listingUuid`.
   * @param {string} platform - Target platform
   * @param {string|number} itemId - Platform item ID
   * @param {string} voolistListingId - Voolist listing ID (for backend reporting)
   * @param {Object} [options]
   * @param {Object} [options.replacement] - The listing the new post will be published from, as its create
   *   sends it (buildExtensionPayload, with the marketplace's aspects)
   * @param {string} [options.listingUuid] - The uuid OfferUp reads the listing by (the post fact `listingUuid`)
   * @returns {Promise<Object>}
   */
  async deleteListing (platform, itemId, voolistListingId, { replacement, listingUuid } = {}) {
    return sendForMarketplace(platform, 'VOOLIST_EXTENSION_DELETE_LISTING', {
      platform, itemId, voolistListingId, ...(listingUuid ? { listingUuid } : {}), ...(replacement ? { replacement } : {}),
    });
  },

  /**
   * Take a listing off sale on a marketplace the way that marketplace takes one down — what Bulk delist
   * does: deleted on Depop, Poshmark and Grailed; hidden on Whatnot, which keeps it in the seller's Whatnot
   * inventory, and on OfferUp, which archives it — each still linked to the listing (`takedownHidesPost`). The
   * extension reports what it did.
   *
   * An extension from before this call (no `delistListing` capability) only deletes: for a marketplace whose
   * takedown deletes, that is the same thing, so it is asked to delete; for one whose takedown hides, the
   * seller is told to update rather than have the listing deleted.
   *
   * @param {string} platform - Target platform
   * @param {string|number} itemId - Platform item ID
   * @param {string} voolistListingId - Voolist listing ID (for backend reporting)
   * @param {Object} [options]
   * @param {string} [options.listingUuid] - The uuid OfferUp reads the listing by; other marketplaces ignore it
   * @returns {Promise<Object>} The marketplace's takedown answer: `{ success: true }`, with `alreadyHidden`
   *   or `notFound` when nothing needed doing; or a failure
   */
  async delistListing (platform, itemId, voolistListingId, { listingUuid } = {}) {
    const capabilities = await voolistExtension.getCapabilities();
    if (Array.isArray(capabilities) && !capabilities.includes(DELIST_CAPABILITY)) {
      if (takedownHidesPost(platform)) return { success: false, extensionOutdated: true, error: updateExtensionMessage(platform) };
      return voolistExtension.deleteListing(platform, itemId, voolistListingId);
    }
    return sendForMarketplace(platform, 'VOOLIST_EXTENSION_DELIST_LISTING', {
      platform, itemId, voolistListingId, ...(listingUuid ? { listingUuid } : {}),
    });
  },

  /**
   * Import listings from a platform via the extension
   * Used for platforms like Depop where server-side fetch is blocked by Cloudflare, and Grailed, Whatnot,
   * OfferUp and Vestiaire, which are only ever read from the seller's browser. A Grailed import reads the seller's
   * listings for sale and their drafts, one listing at a time; a Whatnot import reads Seller Hub's lists
   * of the Buy It Now listings on the Marketplace and the drafts, page by page; an OfferUp import reads My
   * items' listings for sale, page by page, then each one in full; a Vestiaire import reads the seller's listings
   * and drafts, page by page, each draft in full.
   * @param {string} platform - Platform to import from ('depop', 'grailed', 'whatnot', 'offerup', 'vestiaire', etc.)
   * @returns {Promise<Object>} { success, imported, skipped, error } — or a staged import, as Voolist
   *   answers it (`staged`, `listings`, …), with `partial` and `warning` when the read stopped part-way
   */
  async importListings (platform) {
    return sendForMarketplace(platform, 'VOOLIST_EXTENSION_IMPORT_LISTINGS', { platform }, 60000);
  },

  // ==================== Vinted Reference Data ====================

  /**
   * Get Vinted category tree
   * @returns {Promise<Object>}
   */
  async getVintedCatalogs () {
    return sendToExtension('VOOLIST_EXTENSION_GET_VINTED_CATALOGS');
  },

  /**
   * Get Vinted brands for a category
   * @param {number} categoryId
   * @param {string} query - Optional search query
   * @returns {Promise<Object>}
   */
  async getVintedBrands (categoryId, query) {
    return sendToExtension('VOOLIST_EXTENSION_GET_VINTED_BRANDS', { categoryId, query });
  },

  /**
   * Get Vinted sizes for a catalog
   * @param {number|string} catalogIds
   * @returns {Promise<Object>}
   */
  async getVintedSizes (catalogIds) {
    return sendToExtension('VOOLIST_EXTENSION_GET_VINTED_SIZES', { catalogIds });
  },

  /**
   * Get Vinted conditions for a category
   * @param {number} catalogId
   * @returns {Promise<Object>}
   */
  async getVintedConditions (catalogId) {
    return sendToExtension('VOOLIST_EXTENSION_GET_VINTED_CONDITIONS', { catalogId });
  },

  /**
   * Get Vinted colors
   * @returns {Promise<Object>}
   */
  async getVintedColors () {
    return sendToExtension('VOOLIST_EXTENSION_GET_VINTED_COLORS');
  },

  /**
   * Get Vinted package sizes for a category
   * @param {number} catalogId
   * @returns {Promise<Object>}
   */
  async getVintedPackageSizes (catalogId) {
    return sendToExtension('VOOLIST_EXTENSION_GET_VINTED_PACKAGE_SIZES', { catalogId });
  },

  // ==================== Single Listing Fetch ====================

  /**
   * Fetch a single Depop listing by slug
   * @param {string} slug - Depop product slug
   * @returns {Promise<Object>} { success, listing }
   */
  async getDepopListing (slug) {
    return sendToExtension('VOOLIST_EXTENSION_GET_DEPOP_LISTING', { slug });
  },

  /**
   * Fetch a single Poshmark listing by postId
   * @param {string} postId - Poshmark post ID
   * @returns {Promise<Object>} { success, listing }
   */
  async getPoshmarkListing (postId) {
    return sendToExtension('VOOLIST_EXTENSION_GET_POSHMARK_LISTING', { postId });
  },

  /**
   * One Grailed listing as Grailed reads it (`GET /api/listings/{id}`), for Pull updates — without the
   * seller's address or Grailed account, which never leave the browser. A read needs no Grailed login.
   * @param {string|number} postId - Grailed listing id
   * @returns {Promise<{success: boolean, listing?: Object, error?: string, extensionOutdated?: true}>}
   */
  async fetchGrailedListing (postId) {
    return sendForMarketplace('grailed', 'VOOLIST_EXTENSION_GET_GRAILED_LISTING', { postId });
  },

  /**
   * One Whatnot listing as Whatnot's own edit page reads it, for Pull updates: every id as its number, the
   * price in cents, where it sells by channel type only — never the seller's username. A deleted listing
   * reads as one (`status: 'deleted'`).
   * @param {string|number} postId - The Whatnot listing's number
   * @returns {Promise<{success: boolean, listing?: Object, error?: string, extensionOutdated?: true}>}
   */
  async fetchWhatnotListing (postId) {
    return sendForMarketplace('whatnot', 'VOOLIST_EXTENSION_GET_WHATNOT_LISTING', { postId });
  },

  /**
   * One OfferUp listing as OfferUp reads it, for Pull updates: its item number and uuid, its state, the price as
   * OfferUp writes it, its condition as the number OfferUp reads it as, its category, item details, photos at full
   * size and pickup place — never the owner's profile. OfferUp reads a listing by its uuid, and the extension
   * checks the read names the item number. A listing OfferUp removed for breaking its rules reads `state: 'DELETED'`
   * with `isRemoved: true`, which the server keeps linked as removed — nothing posts it again; only `DELETED` without
   * `isRemoved` is taken as gone. A listing OfferUp could not read is a failure to try again (`postUnreadable`), never
   * a listing gone.
   * @param {string|number} postId - The OfferUp listing's item number
   * @param {string} listingUuid - Its uuid (the post fact `listingUuid`)
   * @returns {Promise<{success: boolean, listing?: Object, error?: string, extensionOutdated?: true}>}
   */
  async fetchOfferupListing (postId, listingUuid) {
    return sendForMarketplace('offerup', 'VOOLIST_EXTENSION_GET_OFFERUP_LISTING', { postId, listingUuid });
  },

  /**
   * One Vestiaire listing, for Pull updates, as the seller's own Vestiaire listings show it — the one item shape
   * Voolist's server reads (`readPulledVestiaireListing`): `{ kind: 'listing', productId, state, brandName, title,
   * price: { cents, currency }, productPath, … }`, what Vestiaire's listings do not show left null. Only a listing
   * in a state Voolist knows is read out: one not found on the pages read, or in a state not recorded yet, answers
   * `postUnreadable` — never a listing gone.
   * @param {string|number} productId - The Vestiaire listing's product id
   * @returns {Promise<{success: boolean, listing?: Object, error?: string, postUnreadable?: true, extensionOutdated?: true}>}
   */
  async fetchVestiaireListing (productId) {
    return sendForMarketplace('vestiaire', 'VOOLIST_EXTENSION_GET_VESTIAIRE_LISTING', { postId: productId });
  },

  // ==================== Whatnot Reference Data ====================

  /**
   * The shipping profiles a seller is offered for a Whatnot category — the category's suggested ones and the
   * seller's own, read live from Whatnot, as they differ by country and seller — and the one the package
   * weight fits, which the form pre-selects (the smallest weight-based profile whose weight holds it).
   * @param {string|number} categoryId - The Whatnot category's number
   * @param {(number|null)} [packageWeightGrams] - The package weight in grams, as Voolist serves it
   * @returns {Promise<{success: boolean, profiles?: Array<{id: string, name: (string|null), weightAmount:
   *   (number|null), weightScale: (string|null), inferredType: (string|null), section: (string|null)}>,
   *   pickedByWeight?: (string|null), error?: string, extensionOutdated?: true}>} ids as digit strings;
   *   `pickedByWeight` null without a weight or a profile that holds it
   */
  async getWhatnotShippingProfiles (categoryId, packageWeightGrams = null) {
    return sendForMarketplace('whatnot', 'VOOLIST_EXTENSION_GET_WHATNOT_SHIPPING_PROFILES', { categoryId, weightGrams: packageWeightGrams });
  },

  // ==================== Grailed Reference Data ====================

  /**
   * Search Grailed's designers, as Grailed's own sell form does.
   * @param {string} query - What the seller typed
   * @param {'menswear'|'womenswear'} [department] - Only designers Grailed lists in this department
   * @returns {Promise<{success: boolean, designers?: Array<{id: number, name: string, slug: string,
   *   departments: string[], collaborations: Array<{name: string, designerIds: number[]}>}>,
   *   error?: string, extensionOutdated?: true}>}
   */
  async searchGrailedDesigners (query, department) {
    return sendForMarketplace('grailed', 'VOOLIST_EXTENSION_SEARCH_GRAILED_DESIGNERS', { query, department });
  },

  // ==================== Vestiaire Reference Data ====================

  /**
   * Search Vestiaire's brands — the only ones Vestiaire lists: the brand named exactly as typed first, then names
   * starting with it, then names with a word that does, at most 20, never a brand Vestiaire bans. Names compare case,
   * spaces and accents aside, as vestiaire-brand-search.js compares them. `exact` is the brand named as typed, when
   * Vestiaire lists one it accepts; `exactBanned` says Vestiaire lists that name but does not accept it.
   * @param {string} query - What the seller typed, or the listing's brand to pre-fill the picker with
   * @returns {Promise<{success: boolean, brands?: Array<{id: string, name: string}>, exact?: ({id: string, name: string}|null),
   *   exactBanned?: boolean, error?: string, extensionOutdated?: true}>}
   */
  async searchVestiaireBrands (query) {
    return sendForMarketplace('vestiaire', 'VOOLIST_EXTENSION_SEARCH_VESTIAIRE_BRANDS', { query });
  },

  // ==================== Depop Reference Data ====================

  /**
   * Get Depop attributes (genders, groups, productTypes, colours)
   * @returns {Promise<Object>}
   */
  async getDepopAttributes () {
    return sendToExtension('VOOLIST_EXTENSION_GET_DEPOP_ATTRIBUTES');
  },

  /**
   * Get Depop category filters (departments, groups, product types hierarchy)
   * @returns {Promise<Object>}
   */
  async getDepopCategoryFilters () {
    return sendToExtension('VOOLIST_EXTENSION_GET_DEPOP_CATEGORY_FILTERS');
  },

  /**
   * Get Depop sizes for a specific department/group/productType combo
   * @param {string} department - menswear, womenswear, kidswear, everything-else
   * @param {string} group - footwear, clothing, etc.
   * @param {string} productType - oxfords, trainers, etc.
   * @returns {Promise<Object>} { success, required, regions: { US: { variantSetId, sizes: [{id, name}] } } }
   */
  async getDepopSizes (department, group, productType) {
    return sendToExtension('VOOLIST_EXTENSION_GET_DEPOP_SIZES', { department, group, productType });
  },

  /**
   * Ask whether the seller's Depop account can publish right now, and which shipping its listings
   * will use. Call this BEFORE anything is delisted: Delist & Relist removes the Depop post first
   * and re-creates it afterwards, so a post must never be taken down that cannot go back.
   *
   * Read the answer as three states, never two. `publishReadiness` is 'unknown' whenever the check
   * could not be made — the browser was unreachable, or the seller's extension is too old to know
   * the question and answered nothing at all. **'unknown' means carry on**, never skip: treating a
   * check that did not run as a refusal is the mistake this exists to prevent.
   *
   * `labelPricing` is the fact only the browser can establish. 'none' means Depop prices no postage
   * labels for this account, so its listings must carry a flat shipping cost of their own — combine
   * it with the amounts saved on the account and on each listing to know whether a re-create would
   * be refused. 'none' is only ever returned because Depop said so.
   *
   * A 'blocked' answer names its cause: 'otherAccount' is Chrome signed in to a different Depop
   * account than the one connected to Voolist, so a relist would take down posts that login does
   * not hold; 'signedOut' is no Depop session in this browser; 'sellingNotSetUp' is Depop saying the
   * account cannot sell yet, which is the seller who connected Depop to Voolist but never finished
   * Depop's own payment setup; 'shipFromAddressMissing' is a US account with no address it ships
   * from, whose listings Depop refuses; 'countryNotSupported' is Depop placing the account in a
   * country the extension holds no location for. For the last three a re-created post would be
   * refused and the one taken down would not come back. Each rests on proof — a login whose account
   * Chrome cannot tell, and an account whose country Depop did not report, are never blocked. An
   * extension too old to know a reason sends none, so treat an unrecognised or missing
   * `blockedReason` as "we could not reach the account" rather than assuming which one it was.
   *
   * @returns {Promise<Object>} { success, publishReadiness: 'ready'|'blocked'|'unknown',
   *   labelPricing: 'available'|'none'|'unknown',
   *   blockedReason?: 'otherAccount'|'signedOut'|'sellingNotSetUp'|'shipFromAddressMissing'|'countryNotSupported',
   *   detail?: string }
   */
  async checkDepopPublishReadiness () {
    return sendToExtension('VOOLIST_EXTENSION_CHECK_DEPOP_PUBLISH_READINESS');
  },

  // ==================== Poshmark Reference Data ====================

  /**
   * Get Poshmark catalog (departments, categories, subcategories, sizes, colors, brands)
   * @returns {Promise<Object>}
   */
  async getPoshmarkCatalog () {
    return sendToExtension('VOOLIST_EXTENSION_GET_POSHMARK_CATALOG');
  },

  /**
   * Get Poshmark popular style tags
   * @returns {Promise<Object>}
   */
  async getPoshmarkStyleTags () {
    return sendToExtension('VOOLIST_EXTENSION_GET_POSHMARK_STYLE_TAGS');
  },

  /**
   * Trigger processing of pending platform syncs (from bulk edit)
   * The extension will fetch pending listings and update them on Depop/Poshmark
   * @returns {Promise<Object>}
   */
  async triggerPendingPlatformSyncs () {
    return sendToExtension('VOOLIST_EXTENSION_TRIGGER_PENDING_SYNCS', {});
  },

  // ==================== Voolist login ====================

  /**
   * Tell the extension the signed-in Voolist user changed — a login, a logout, a switch to another
   * account. The extension keeps this browser's Voolist login so it can work while Voolist is closed,
   * and this is how it learns to stop working for an account the seller has left: it reads the login
   * from this page again, or forgets it when nobody is signed in.
   *
   * Never throws, and waits no longer than a ping: no extension is a normal answer, and an older one
   * replies that it does not know the message, which is just as harmless.
   *
   * @returns {Promise<void>}
   */
  async notifyAuthChanged () {
    try {
      await sendToExtension('VOOLIST_EXTENSION_AUTH_CHANGED', {}, PING_TIMEOUT);
    } catch {
      // No extension listening.
    }
  },

  // ==================== Platform Auto-Connect ====================

  /**
   * Connect a platform via the extension (auto-connect flow).
   * Fast path: returns { success, connected } if the seller is already signed in there.
   * Slow path: returns { success, pending, waitingFor } — the connect waits on the seller, to sign in
   * on the marketplace tab it opened (`waitingFor: 'login'`) or, for Grailed, Whatnot and OfferUp, first to
   * allow Voolist on the marketplace's site in the window it opened (`waitingFor: 'access'`) — and the
   * extension pushes PLATFORM_CONNECT_WAITING, PLATFORM_CONNECTED or PLATFORM_CONNECT_CANCELLED as it
   * moves on.
   * @param {string} platform - 'poshmark', 'depop', 'grailed', 'whatnot' or 'offerup'
   * @param {string} marketplaceName - e.g. 'poshmarkUs', 'poshmarkCa', 'depop', 'grailed', 'whatnot', 'offerup'
   * @param {Object} [options]
   * @param {number} [options.timeoutMs] - Milliseconds of silence before giving up
   * @returns {Promise<Object>}
   */
  async connectPlatform (platform, marketplaceName, { timeoutMs = CONNECT_TIMEOUT } = {}) {
    return sendForMarketplace(platform, 'VOOLIST_EXTENSION_CONNECT_PLATFORM', { platform, marketplaceName }, timeoutMs);
  },

  /**
   * Register a listener for push events from the extension.
   * Push events are unsolicited messages (e.g. PLATFORM_CONNECTED after slow-path login).
   * @param {string} action - Push action name (e.g. 'PLATFORM_CONNECTED')
   * @param {Function} callback - Called with the event data
   * @returns {Function} Unsubscribe function
   */
  onPush (action, callback) {
    if (!_pushListeners.has(action)) {
      _pushListeners.set(action, new Set());
    }
    _pushListeners.get(action).add(callback);
    return () => _pushListeners.get(action)?.delete(callback);
  }
};

// ==================== Push Event System ====================
// Receives unsolicited messages from the extension (via VOOLIST_EXTENSION_PUSH)

const _pushListeners = new Map();

if (typeof window !== 'undefined') {
  window.addEventListener('message', (event) => {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type !== 'VOOLIST_EXTENSION_PUSH') return;

    const { action } = event.data;
    const listeners = _pushListeners.get(action);
    if (listeners) {
      listeners.forEach(cb => {
        try { cb(event.data); } catch (e) { /* ignore listener errors */ }
      });
    }
  });
}

export default voolistExtension;
